import React, { useState } from "react";
import { DateRangePicker } from "oolib";
import styled from "styled-components";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
} from "./BaseIntegrationInterface";
import { BaseOAuthInterface } from "./BaseOAuthInterface";

// ============================================
// ZOOM ICON
// ============================================

export const ZoomIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#2D8CFF" />
    <rect x="5" y="7" width="10" height="10" rx="2" fill="white" />
    <polygon points="16,9 20,6.5 20,17.5 16,15" fill="white" />
  </svg>
);

// ============================================
// STYLED COMPONENTS
// ============================================

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

// End date defaults to today (user can still change it). YYYY-MM-DD matches the
// DatePicker value format used across the other integration interfaces.
const todayIso = () => new Date().toISOString().slice(0, 10);

// ============================================
// ZOOM INTERFACE
// ============================================

/**
 * OAuth-based interface for Zoom.
 * Step 1: User connects via OAuth (BaseOAuthInterface handles this)
 * Step 2: User selects date range -> Import Meetings
 */
export const ZoomInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
}) => {
  const [dateRange, setDateRange] = useState([todayIso(), todayIso()]);
  const [fromDate, toDate] = dateRange;

  const handleSubmit = (accessToken) => {
    if (!fromDate || !toDate) return;
    onSubmit({
      apiKey: accessToken,
      adapterOptions: {
        fromDate,
        toDate,
      },
    });
  };

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Zoom</InterfaceHeader>
      <InterfaceDescription>
        Import meeting recordings and transcripts from your Zoom account.
        Connect your Zoom account, then select a date range.
      </InterfaceDescription>

      <BaseOAuthInterface providerId="zoom" providerName="Zoom">
        {(accessToken) => (
          <>
            <DateRangeRow>
              <DateRangePicker
                id="zoom-date-range"
                label="Date Range"
                value={dateRange}
                onChange={(k, v) => setDateRange(v)}
              />
            </DateRangeRow>

            <HelpText>
              Select a date range to import meetings with recordings. Transcripts
              will be included when available.
            </HelpText>

            <InterfaceActions>
              <ButtonPrimary
                onClick={() => handleSubmit(accessToken)}
                disabled={!fromDate || !toDate || isLoading}
                isLoading={isLoading}
              >
                Import Meetings
              </ButtonPrimary>
            </InterfaceActions>
          </>
        )}
      </BaseOAuthInterface>
    </InterfaceContainer>
  );
};

// ============================================
// INTEGRATION META
// ============================================

export const zoomIntegrationMeta = {
  id: "zoom",
  name: "Zoom",
  description: "Import meeting recordings and transcripts from Zoom",
};

export default ZoomInterface;
